/*
BUOY MODEL
Auto-generated by: https://github.com/pmndrs/gltfjsx
author: debbie-s (https://sketchfab.com/debbie-s)
license: CC-BY-4.0 (http://creativecommons.org/licenses/by/4.0/)
source: https://sketchfab.com/3d-models/buoy-1f7f03384deb436193ebae0e9adde2e2
title: buoy
*/

import React, { Suspense, useRef, useState } from "react";
import { extend, useFrame, useThree } from "@react-three/fiber";
import {
  useGLTF,
  Html,
  Instances,
  Instance,
  useTexture,
  useVideoTexture,
  shaderMaterial,
} from "@react-three/drei";
import * as THREE from "three";

import Embed, { EmbedTFG } from "../Embed";
import { gestnerWave } from "./Ocean";
import { EquirectangularDistance, HarvesineBearing } from "./Boat";
import { quadFragment, quadVertex } from "./data/shaders";

// array of colors depending on buoy type
const buoycolor = {
  JELLYFISH: "#7600bc",
  SEAWEED: "#00ab41",
  PHOTO: "#e69b00",
  VIDEO: "#dc6601",
};

// instancing of all the buoys in the scene
export function Buoys({ currentBuoy, beacons, boat, weather }) {
  const { nodes } = useGLTF("./assets/buoy/scene.gltf"); // load buoy model

  // buoy info
  const data = [];
  for (var i in beacons) data.push(beacons[i]);

  return (
    <Instances geometry={nodes.Trompo_Mat_0.geometry} castShadow>
      <meshPhongMaterial />
      {data.map((props, i) => (
        <Buoy
          key={i}
          {...props}
          currentBuoy={currentBuoy}
          boat={boat}
          weather={weather}
        />
      ))}
    </Instances>
  );
}

useGLTF.preload("./assets/buoy/scene.gltf");

// single instance
function Buoy({ currentBuoy, boat, weather, ...props }) {
  const ref = useRef();
  const [clicked, click] = useState(false);

  const { scene } = useThree();

  let buoyancy = {
    normal: new THREE.Vector3(),
    position: new THREE.Vector3(),
  };
  var waves, time;

  const windDirection = weather["windDirection"];
  const boatDirection = [
    -Math.sin(boat.direction) + 0.01,
    Math.cos(boat.direction) + 0.01,
  ];
  const d =
    0.01 *
    EquirectangularDistance(
      boat.latitude,
      boat.longitude,
      props.coordinates.lat,
      props.coordinates.lng
    );

  const angle = HarvesineBearing(
    boat.latitude,
    boat.longitude,
    props.coordinates.lat,
    props.coordinates.lng
  );

  const buoyPos = [d * Math.sin(angle), 0, d * Math.cos(angle)];
  var vertex = new THREE.Vector2(buoyPos[0], buoyPos[2]);

  useFrame(() => {
    // update position (only)
    waves = scene.children[9].waves;
    time = scene.children[9].time;
    buoyancy = gestnerWave(
      waves,
      vertex,
      time,
      windDirection,
      boatDirection,
      boat.speed
    );
    ref.current.position.copy(buoyancy.position);
  }); // add buoyancy effect

  const { nodes, materials } = useGLTF("./assets/buoy/scene.gltf"); // load buoy model

  // return whole buoy model
  return (
    <group position-y={1}>
      <group
        {...props}
        position={buoyPos}
        scale={0.02}
        name={"buoy"}
        ref={ref}
        onClick={() => click(!clicked)}
      >
        <mesh
          name={"bottom"}
          geometry={nodes.Trompo_Mat1_0.geometry}
          material={materials["Mat.1"]}
        />
        <mesh
          name={"top"}
          geometry={nodes.Trompo_Mat3_0.geometry}
          material={materials["Mat.3"]}
        />
        <mesh
          name={"stripe"}
          geometry={nodes.Trompo_Mat2_0.geometry}
          material={materials["Mat.2"]}
        />
        <Instance name={"main"} color={buoycolor[props.buoyType]} />

        {/* {/* show buoy info on click on top of model  */}
        {clicked ? <BuoyInfo {...props} currentBuoy={currentBuoy} /> : <></>}

        {/* show buoy if clicked on map */}
        {/* {currentBuoy !== null && currentBuoy._id === props._id ? (
        <BuoyInfo {...props} currentBuoy={currentBuoy} />
        ) : (
          <></>
        )} */}
      </group>
    </group>
  );
}

// html element to show information about the buoy
function BuoyInfo({ currentBuoy, ...props }) {
  const { camera } = useThree();
  const inforef = useRef();

  useFrame(() => {
    inforef.current.lookAt(camera.position);
  }, [camera]);

  return (
    <mesh ref={inforef} scale={600} position={props.position}>
      <planeGeometry />
      <meshStandardMaterial opacity={0} transparent depthWrite={false} />
      <Html distanceFactor={1.5} position={[0, 0.5, 0]} transform>
        {/* <Embed currentBuoy={props} /> */}
        <EmbedTFG currentBuoy={props} />
      </Html>
    </mesh>
  );
}

// screen quad to show pictures in logbook
const PhotoMaterial = new shaderMaterial(
  {
    map: new THREE.Texture(),
    imageRatio: 16 / 9,
    screenRatio: 16 / 9,
  },
  quadVertex,
  quadFragment
);

extend({ PhotoMaterial });

export function BuoyImage({ path }) {
  // image
  const texture = useTexture(path);
  const imageRatio = texture.image.width / texture.image.height;
  // video
  // const texture = useVideoTexture("10.mp4");
  // const imageRatio = texture.image.videoWidth / texture.image.videoHeight;

  const viewport = useThree((state) => state.viewport);
  const screenRatio = viewport.width / viewport.height;

  return (
    <mesh>
      <planeGeometry args={[1, 1]} />
      <photoMaterial
        map={texture}
        imageRatio={imageRatio}
        screenRatio={screenRatio}
      />
    </mesh>
  );
}
